import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import VolunteerList from '../../components/admin/volunteer/VolunteerList';
import VolunteerForm from '../../components/admin/volunteer/VolunteerForm';
import VolunteerCredentialModal from '../../components/admin/volunteer/VolunteerCredentialModal';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';

const VolunteerPage = () => {
  const { volunteers, addVolunteer, updateVolunteer, deleteVolunteer, fetchVolunteers } = useData();
  const { isAuthenticated, role, user } = useAuth();
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [currentVolunteer, setCurrentVolunteer] = useState(null);
  const [credentialModalOpen, setCredentialModalOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isFirstRender = useRef(true);
  const refreshIntervalRef = useRef(null);

  // Check if user is authenticated and has admin role
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    console.log('Current user role:', role);

    if (role !== 'admin') {
      console.log('User not admin, redirecting to appropriate page');
      navigate('/');
      return;
    }

    console.log('User authenticated as admin:', user);
  }, [isAuthenticated, role, navigate, user]);

  // Memoized function to load volunteers
  const loadVolunteers = useCallback(async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setInitialLoading(true);
      } else {
        setIsRefreshing(true);
      }

      await fetchVolunteers();
      console.log('Volunteers loaded successfully');
    } catch (error) {
      console.error('Error loading volunteers:', error);
      // Only show alert on initial load, not on background refreshes
      if (showLoadingState) {
        alert('Failed to load volunteers. Please refresh the page.');
      }
    } finally {
      if (showLoadingState) {
        setInitialLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, [fetchVolunteers]);

  // Initial load of volunteers
  useEffect(() => {
    if (isFirstRender.current) {
      loadVolunteers(true);
      isFirstRender.current = false;
    }
  }, [loadVolunteers]);

  // Set up refresh interval
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Set up a new interval
    refreshIntervalRef.current = setInterval(() => {
      // Only refresh if not already refreshing and not showing the form
      if (!isRefreshing && !showForm) {
        loadVolunteers(false);
      }
    }, 30000); // Refresh every 30 seconds

    // Clean up the interval when the component unmounts
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loadVolunteers, isRefreshing, showForm]);

  const handleAddClick = () => {
    setCurrentVolunteer(null);
    setShowForm(true);
  };

  const handleEditClick = (volunteer) => {
    // Map the volunteer data to match the form fields
    const mappedVolunteer = {
      id: volunteer.id,
      name: volunteer.volunteerName,
      phone: volunteer.mobile,
      address: volunteer.village,
      location: volunteer.district,
      email: volunteer.username,
      status: volunteer.status
    };
    setCurrentVolunteer(mappedVolunteer);
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Are you sure you want to delete this volunteer? This will also permanently delete the associated user account.')) {
      // Find the delete button and show loading state on it
      const deleteButton = document.querySelector(`button[data-volunteer-id="${id}"]`);
      if (deleteButton) {
        deleteButton.disabled = true;
        deleteButton.innerHTML = 'Deleting...';
      }

      try {
        const success = await deleteVolunteer(id);

        if (success) {
          // Refresh the volunteers list without showing loading state
          await loadVolunteers(false);

          // Show success message after state updates
          setTimeout(() => {
            alert('Volunteer and their associated user account have been successfully deleted.');
          }, 100);
        } else {
          throw new Error('Delete operation returned false');
        }
      } catch (error) {
        console.error('Error in handleDeleteClick:', error);

        // Reset the button state
        if (deleteButton) {
          deleteButton.disabled = false;
          deleteButton.innerHTML = 'Delete';
        }

        // Show error message after a short delay
        setTimeout(() => {
          alert('Failed to delete the volunteer. Please try again.');
        }, 100);
      }
    }
  };

  const handleFormSubmit = async (formData) => {
    // Use a local loading state for the form submission
    const formSubmitButton = document.getElementById('volunteerFormSubmitButton');
    if (formSubmitButton) {
      formSubmitButton.disabled = true;
      formSubmitButton.textContent = 'Saving...';
    }

    try {
      if (currentVolunteer) {
        // Update existing volunteer
        console.log('Updating volunteer with ID:', currentVolunteer.id);

        const updatedVolunteer = await updateVolunteer(currentVolunteer.id, formData);
        console.log('Volunteer updated successfully:', updatedVolunteer);

        setShowForm(false);

        // Refresh volunteers without showing loading state
        await loadVolunteers(false);

        // Show success message after state updates
        setTimeout(() => {
          alert('Volunteer updated successfully!');
        }, 100);
      } else {
        // Add new volunteer
        console.log('Creating new volunteer with data:', formData);

        try {
          const newVolunteer = await addVolunteer(formData);
          console.log('Volunteer created successfully:', newVolunteer);

          setShowForm(false);

          // Refresh volunteers without showing loading state
          await loadVolunteers(false);

          // Check if we have user credentials in the response
          if (newVolunteer && newVolunteer.user_credentials) {
            // Show credentials modal after a short delay to avoid UI flicker
            setTimeout(() => {
              // Log the credentials for debugging
              console.log('Volunteer credentials:', newVolunteer.user_credentials);

              setCredentials({
                username: newVolunteer.user_credentials.username,
                loginEmail: newVolunteer.user_credentials.login_email || newVolunteer.user_credentials.username,
                password: newVolunteer.user_credentials.password,
                volunteerName: newVolunteer.name
              });
              setCredentialModalOpen(true);
            }, 100);
          } else {
            // If no credentials in response, show a generic success message
            setTimeout(() => {
              alert('Volunteer created successfully!');
            }, 100);
          }
        } catch (createError) {
          console.error('Error creating volunteer:', createError);

          // Show detailed error message
          setTimeout(() => {
            if (createError.response && createError.response.data) {
              const errorData = createError.response.data;

              if (errorData.detail) {
                alert(`Error: ${errorData.detail}`);
              } else if (errorData.email) {
                // Special handling for email errors
                alert(`Email Error: ${errorData.email}`);
              } else if (errorData.username) {
                // Special handling for username errors
                alert(`Username Error: ${errorData.username}`);
              } else if (errorData.error) {
                // Special handling for general errors
                alert(`Error: ${errorData.error}`);
              } else if (typeof errorData === 'object') {
                const errorMessages = Object.entries(errorData)
                  .map(([field, value]) => `${field}: ${value}`)
                  .join('\n');
                alert(`Error creating volunteer:\n${errorMessages}`);
              } else {
                alert(`Error: ${JSON.stringify(errorData)}`);
              }
            } else if (createError.message) {
              alert(`Error: ${createError.message}`);
            } else {
              alert('Failed to create volunteer. Please try again.');
            }
          }, 100);

          throw createError; // Re-throw to prevent further processing
        }
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      // Error is already handled in the nested try-catch
    } finally {
      // Reset the button state
      if (formSubmitButton) {
        formSubmitButton.disabled = false;
        formSubmitButton.textContent = currentVolunteer ? 'Update Volunteer' : 'Add Volunteer';
      }
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.pageHeader}>
        <div style={styles.titleContainer}>
          <span style={styles.titleIcon}>👨‍🌾</span>
          <h1 style={styles.title}>Volunteers Management</h1>
        </div>
        <p style={styles.subtitle}>
          Register and manage volunteer information for better agricultural support
        </p>
      </div>

      <div style={styles.actionHeader}>
        <div style={styles.stats}>
          <div style={styles.statItem}>
            <span style={styles.statValue}>{volunteers.length}</span>
            <span style={styles.statLabel}>Total Volunteers</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statValue}>
              {volunteers.filter(v => v.district).length}
            </span>
            <span style={styles.statLabel}>Districts</span>
          </div>
        </div>
        <button
          onClick={handleAddClick}
          style={styles.addButton}
          disabled={initialLoading || isRefreshing}
        >
          <span style={styles.buttonIcon}>➕</span>
          Add New Volunteer
        </button>
      </div>

      <div style={styles.content}>
        {showForm ? (
          <VolunteerForm
            volunteer={currentVolunteer}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        ) : (
          <div style={{ position: 'relative' }}>
            {/* Only show loading spinner on initial load when no volunteers exist */}
            {initialLoading && volunteers.length === 0 ? (
              <div style={styles.loadingContainer}>
                <div style={styles.loadingSpinner}></div>
                <p>Loading volunteers...</p>
              </div>
            ) : (
              <>
                <VolunteerList
                  volunteers={volunteers}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                />

                {/* Show a subtle refreshing indicator when refreshing in the background */}
                {isRefreshing && (
                  <div style={styles.refreshingIndicator}>
                    <div style={styles.smallSpinner}></div>
                    <span>Refreshing...</span>
                  </div>
                )}

                {/* Show empty state if no volunteers exist */}
                {!initialLoading && volunteers.length === 0 && (
                  <div style={styles.emptyState}>
                    <p>No volunteers found. Click "Add Volunteer" to create one.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <VolunteerCredentialModal
        open={credentialModalOpen}
        credentials={credentials}
        onClose={() => setCredentialModalOpen(false)}
      />
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  pageHeader: {
    marginBottom: '1.5rem',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '1rem',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    fontSize: '2rem',
    marginRight: '0.75rem',
    color: 'var(--primary-color)',
  },
  title: {
    margin: 0,
    color: 'var(--text-color)',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  subtitle: {
    margin: '0.5rem 0 0 0',
    color: 'var(--text-light)',
    fontSize: '1rem',
  },
  actionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: 'var(--card-shadow)',
  },
  stats: {
    display: 'flex',
    gap: '2rem',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: 'var(--primary-color)',
  },
  statLabel: {
    fontSize: '0.9rem',
    color: 'var(--text-light)',
  },
  addButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  buttonIcon: {
    fontSize: '1rem',
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: 'var(--card-shadow)',
    overflow: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '50%',
    borderTop: '4px solid #e74c3c',
    animation: 'spin 1s linear infinite',
    marginBottom: '1rem',
  },
  refreshingIndicator: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    display: 'flex',
    alignItems: 'center',
    padding: '5px 10px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    fontSize: '0.8rem',
    color: '#666',
    zIndex: 5,
  },
  smallSpinner: {
    width: '12px',
    height: '12px',
    border: '2px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '50%',
    borderTop: '2px solid #e74c3c',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    color: '#666',
    textAlign: 'center',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
};

export default VolunteerPage;
